import {useRef, useState} from 'react';
import {useToast} from '../../hooks/use-toast';
import useVisitSupplierReferences, {referenceValidation} from './useVisitSupplierReferences';
import {canonicalVisitItemValidationMessage, validateCanonicalVisitItems, createRequestKey} from './vehicleVisitCardModel';

export default function useUnifiedVisitItems(visit, controller, canEditReferences) {
    const references = useVisitSupplierReferences(visit.id, canEditReferences);
    const {toast} = useToast();
    const lock = useRef(false), [saving, setSaving] = useState(false);
    const busy = saving || controller.isSaving;
    const warn = description => toast({title: 'راجع البنود', description, variant: 'destructive'});
    const updateFinancial = (index, field, value) => {
        if (busy || references.uncertain) return;
        if (field === 'supplierRecordMode' && value === 'reference') {
            const item = controller.items[index];
            if (item?.itemType !== 'supplier' || item.id) return;
            if (references.add(item)) controller.deleteItem(index);
        } else controller.updateItem(index, field, value);
    };
    const updateReference = (key, field, value) => {
        if (busy) return;
        if (field === 'customerBillable') {
            const row = references.rows.find(r => r.key === key);
            if (!row || row.id || !references.discardDraft(key)) return;
            controller.setItems(current => [...current, {clientKey: createRequestKey('visit-item', visit.id), itemType: 'supplier',
                supplierId: row.supplierId || null, name: row.name, quantity: 1, qty: 1,
                price: Number(row.referenceAmount || 0), total: Number(row.referenceAmount || 0), customerBillable: value}]);
        } else references.update(key, field, value);
    };
    const save = async () => {
        if (lock.current || controller.isSaving || controller.finalized) return;
        const validation = validateCanonicalVisitItems(controller.items);
        const invalid = validation ? canonicalVisitItemValidationMessage(validation) : referenceValidation(references.rows);
        if (invalid) {warn(invalid); return;}
        if (controller.items.some(item => item.itemType === 'supplier' && !item.supplierId)) {warn('اختر المورد لكل بند شراء قبل الحفظ.'); return;}
        lock.current = true; setSaving(true);
        try {
            const hadReferences = references.pending;
            const result = await references.saveAll();
            if (!result.ok) return;
            if (controller.hasUnsavedFinancialChanges || !hadReferences) {
                const ok = await controller.handleSave();
                if (ok === false && result.count) warn('حُفظ التوثيق، وتعذر حفظ البنود المالية. بقيت تعديلاتك ظاهرة؛ أعد حفظ البنود.');
            } else {
                controller.setIsEditing(false);
                toast({title: 'تم حفظ البنود', description: 'حُفظ التوثيق دون تغيير البنود المالية أو إجمالي العميل.'});
            }
        } finally {lock.current = false; setSaving(false);}
    };
    const cancel = () => {
        if (busy) return;
        if (!references.reset()) {warn('أعد حفظ البنود أولًا للتحقق من نتيجة الطلب السابق.'); return;}
        controller.cancelEditing();
    };
    const guarded = action => () => {
        if (busy) return;
        if (references.pending || references.uncertain) {warn('احفظ بنود التوثيق أو ألغِ تعديلها قبل اعتماد الزيارة أو إغلاقها.'); return;}
        action();
    };
    return {references, busy, updateFinancial, updateReference, save, cancel,
        requestClose: guarded(controller.requestCloseVisit), confirmClose: guarded(controller.confirmCloseVisit),
        requestFinalize: guarded(controller.requestFinalize), confirmFinalize: guarded(controller.confirmFinalize)};
}
